import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["sidebarLink", "mobileLink", "dropdownButton", "listItem", "heading", "desktopView", "mobileView"];

  initialize() {
    this.currentActiveIndex = -1;
    this.mainNavHeight = 80;
    this.scrollThreshold = 320;
    this.isScrolling = false;
    this.sections = [];
    this.sectionPositions = [];
    this.headingWidth = 0;
    this.minScreenWidth = 0;
  }

  connect() {
    this.handleStickySidebar();
    
    setTimeout(() => {
      this.calculateMinScreenWidth();
      this.resize();
    }, 300);

    if (this.sidebarLinkTargets.length) {
      this.highlightLink(0);
    }

    this.boundHandleScroll = this.handleScroll.bind(this);
    window.addEventListener("scroll", this.boundHandleScroll);
    
    this.setupSections();
    
    this.updateActiveSection();
    
    this.boundHandleResize = this.setupSections.bind(this);
    window.addEventListener("resize", this.boundHandleResize);
    
    this.boundResizeHandler = this.resize.bind(this);
    window.addEventListener("resize", this.boundResizeHandler);
    window.addEventListener("load", this.boundResizeHandler);
    window.addEventListener("orientationchange", this.boundResizeHandler);
  }
  
  setupSections() {
    this.sections = [];
    this.sectionPositions = [];
    
    this.sidebarLinkTargets.forEach(link => {
      const targetId = link.getAttribute("href").slice(1);
      const section = document.getElementById(targetId);
      if (section) {
        this.sections.push(section);
      }
    });
    
    this.updateSectionPositions();
  }
  
  updateSectionPositions() {
    this.sectionPositions = this.sections.map(section => {
      const rect = section.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      return rect.top + scrollTop - this.mainNavHeight - 50; 
    });
  }

  handleScroll() {
    this.handleStickySidebar();
    
    if (this.isScrolling) return;
    
    this.updateActiveSection();
  }
  
  updateActiveSection() {
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    
    const scrollPosition = currentScroll + window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;
    if (pageHeight - scrollPosition < 50) {
      this.highlightLink(this.sidebarLinkTargets.length - 1);
      return;
    }
    
    let activeIndex = -1;
    
    for (let i = 0; i < this.sectionPositions.length; i++) {
      if (currentScroll >= this.sectionPositions[i]) {
        activeIndex = i;
      } else {
        break;
      }
    }
    
    if (activeIndex !== -1 && activeIndex !== this.currentActiveIndex) {
      this.highlightLink(activeIndex);
    }
  }

  hideAllIndicators() {
    this.listItemTargets.forEach(item => {
      const indicator = item.querySelector(".indicator-sidebar");
      const link = item.querySelector("a");
      if (indicator) indicator.style.display = "none";
      if (link) link.classList.remove("active-link");
    });
  }

  highlightLink(index) {
    if (index < 0 || index >= this.listItemTargets.length) return;
    
    if (index === this.currentActiveIndex) return;

    this.hideAllIndicators();

    const activeItem = this.listItemTargets[index];
    const indicator = activeItem.querySelector(".indicator-sidebar");
    const link = activeItem.querySelector("a");

    if (indicator) indicator.style.display = "inline-block";
    if (link) link.classList.add("active-link");

    this.currentActiveIndex = index;
  }

  scrollToElement(element, index) {
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const elementTop = rect.top + scrollTop;
    
    const scrollPos = elementTop - this.mainNavHeight - 20;
    
    this.isScrolling = true;
    
    this.highlightLink(index);
    
    window.scrollTo({ 
      top: scrollPos, 
      behavior: "smooth" 
    });
    
    setTimeout(() => {
      this.isScrolling = false;
      this.updateSectionPositions();
    }, 1000);
  }

  handleSidebarClick(e) {
    const link = e.target.closest(".sidebar-link");
    if (!link) return;

    e.preventDefault();
    const index = this.sidebarLinkTargets.indexOf(link);
    const targetId = link.getAttribute("href").slice(1);
    const target = document.getElementById(targetId);

    this.scrollToElement(target, index);
  }

  handleMobileClick(e) {
    e.preventDefault();
    const link = e.currentTarget;
    const index = this.mobileLinkTargets.indexOf(link);
    const targetId = link.getAttribute("href").slice(1);
    const target = document.getElementById(targetId);

    if (this.hasDropdownButtonTarget) {
      setTimeout(() => {
        this.dropdownButtonTarget.click();
      }, 50);
    }

    setTimeout(() => {
      this.scrollToElement(target, index);
    }, 100);
  }

  handleStickySidebar() {
    this.element.classList.toggle("scrolled", window.pageYOffset >= this.scrollThreshold);
  }

    calculateMinScreenWidth() {
    if (!this.hasHeadingTarget || this.headingTarget.offsetWidth === 0) return;

    this.headingWidth = this.headingTarget.offsetWidth;
    const screenWidth = window.innerWidth;

    const pagePadding = screenWidth < 768 ? 16
                      : screenWidth < 1024 ? 40
                      : screenWidth < 1280 ? 80
                      : screenWidth < 1536 ? 160
                      : 320;

    const columnGap = 16;
    this.minScreenWidth = (this.headingWidth * 4) + ( 2 * pagePadding) + columnGap;
  }

  resize() {
    if (!this.hasHeadingTarget || !this.hasDesktopViewTarget || !this.hasMobileViewTarget) return;

    this.calculateMinScreenWidth();

    const currentWidth = window.innerWidth;
    const isMobile = currentWidth < (this.minScreenWidth || 768);
    const footer = document.querySelector('footer');
    const dropdownButton = document.getElementById('dropdownBottomButton');
    const backToTopContainer = document.getElementById('back-to-top-container');
    
    if (isMobile) {
      this.desktopViewTarget.classList.add("hidden");
      this.desktopViewTarget.classList.remove("md:flex");
      this.mobileViewTarget.classList.remove("hidden", "md:hidden");
      
      if (backToTopContainer && dropdownButton) {
        backToTopContainer.style.bottom = '4.5rem';
      }
      
      if (footer && dropdownButton) {
        footer.style.paddingBottom = '4.5rem';
      }
    } else {
      this.desktopViewTarget.classList.remove("hidden");
      this.desktopViewTarget.classList.add("md:flex");
      this.mobileViewTarget.classList.add("hidden", "md:hidden");
      
      if (backToTopContainer && dropdownButton) {
        backToTopContainer.style.bottom = '1.5rem';
      }
    }
  }
  
  disconnect() {
    if (this.boundHandleScroll) {
      window.removeEventListener("scroll", this.boundHandleScroll);
    }
    
    if (this.boundHandleResize) {
      window.removeEventListener("resize", this.boundHandleResize);
    }
    
    if (this.boundResizeHandler) {
      window.removeEventListener("resize", this.boundResizeHandler);
      window.removeEventListener("load", this.boundResizeHandler);
      window.removeEventListener("orientationchange", this.boundResizeHandler);
    }
  }
}
